import {
    Button,
    ELanguage,
    Hooks,
    Images,
    MultipleSelect,
    JobCard,
    Row,
    Col,
    MultipleRadio,
    Span,
    colors
} from '@microfrontends/react-components'
import React, { memo, useEffect, useRef, useState } from 'react'
import Image from 'next/image'
import { initialDataProvider } from '@microfrontends/app-shell-v2'

import { EmptyDiv } from '../CompanyDetail/CompanyDetail.css'
import { IJobInfo } from '../JobItem/type'
import {
    ButtonWrapper,
    ImageWrapper,
    JobAmount,
    JobListHeader,
    JobListTitle,
    JobListWrapper,
    SearchBox,
    SearchInput,
    SearchRemoveIcon,
    JobCardWrapper
} from './JobList.css'

import { ICompanyInfo } from '../CompanyDetail/type'
import { useStore } from '../../store'
import { doGetJobList } from './services'
import { UPDATE_USER } from '../constants'
import { decrypt } from '../../helpers/encode'
import NarrowArrow from '../Icons/NarrowArrow'
import { TOption } from '@microfrontends/react-components/components/MultipleRadio/context'

interface IJobListProps {
    companyInfo: ICompanyInfo | null
    t: (text: string) => string
    enablePagination?: boolean
}
const JOB_PER_PAGE_DEFAULT = 20
const JOB_PER_PAGE_MAXIMUM = 200
const EMPTY_IMAGE = `${process.env.NEXT_PUBLIC_IMAGE_URL}/company-assets/images/icon-jobs1x.png`

const JobList = (props: IJobListProps) => {
    const { t, companyInfo, enablePagination } = props
    const { user, langCode, isMobile, isRobot, companyJobs } = useStore().state
    const { useDidUpdateEffect, useDebounce } = Hooks
    const [jobPerPage, setJobPerPage] = useState(enablePagination ? JOB_PER_PAGE_DEFAULT : JOB_PER_PAGE_MAXIMUM)
    const [jobList, setJobList] = useState<IJobInfo[]>(companyJobs?.jobs || [])
    const [totalJobs, setTotalJobs] = useState(isRobot ? companyJobs?.jobTotal || 0 : 0)
    const [page, setPage] = useState(0)
    const [locationId, setLocationId] = useState<number[]>(isRobot ? companyJobs?.jobLocations || [] : [])
    const [searchQuery, setSearchQuery] = useState('')
    const [cityOptions, setCityOptions] = useState([])
    const [isHaveJobs, setIsHaveJobs] = useState(true)

    const jobListRef = useRef<HTMLDivElement>(null)
    const isDidMountedRef = useRef(false)
    const debouncedQuery = useDebounce(searchQuery, 1000)

    const jobPerPageOptions: TOption[] = [
        {
            id: 20,
            name: `${t('Jobs per page')}: 20`
        },
        {
            id: 50,
            name: `${t('Jobs per page')}: 50`
        },
        {
            id: 100,
            name: `${t('Jobs per page')}: 100`
        },
        {
            id: 200,
            name: `${t('Jobs per page')}: 200`
        }
    ]

    const getLocalizedOptions = (data: any) => {
        return data.map(({ id, name, nameEn }: any) => ({
            id,
            name: langCode === ELanguage.EN ? nameEn : name
        }))
    }

    const goToTopJobList = () => {
        // Timeout 20ms to make sure
        setTimeout(() => {
            jobListRef.current?.scrollIntoView({
                behavior: 'smooth',
                block: 'start'
            })
        }, 20)
    }

    const handleNavigatePage = async (page: number) => {
        if (companyInfo?.companyId) {
            isDidMountedRef.current = true
            const { jobs, jobTotal } = await doGetJobList(
                companyInfo?.companyId || 0,
                user?.accessToken || '',
                searchQuery,
                page,
                locationId,
                user?.userId,
                jobPerPage,
                langCode
            )
            setJobList(jobs)
            setPage(page)
            setTotalJobs(jobTotal)
        }
    }

    const handleJobPerPageChange = async (jobPerPage: number) => {
        setJobPerPage(jobPerPage)
        setPage(0)
        isDidMountedRef.current = true
        const { jobs, jobTotal } = await doGetJobList(
            companyInfo?.companyId || 0,
            user?.accessToken || '',
            searchQuery,
            0,
            locationId,
            user?.userId,
            jobPerPage,
            langCode
        )
        setJobList(jobs)
        setTotalJobs(jobTotal)
    }

    const handleViewMoreJob = async (jobPerPage: number) => {
        setJobPerPage(jobPerPage)
        setPage(0)
        const { jobs, jobTotal } = await doGetJobList(
            companyInfo?.companyId || 0,
            user?.accessToken || '',
            searchQuery,
            0,
            locationId,
            user?.userId,
            jobPerPage,
            langCode
        )
        setJobList(jobs)
        setTotalJobs(jobTotal)
    }

    const handleLocationChange = async (locationId: number[]) => {
        setLocationId(locationId)
        setPage(0)
        const { jobs, jobTotal } = await doGetJobList(
            companyInfo?.companyId || 0,
            user?.accessToken || '',
            searchQuery,
            0,
            locationId,
            user?.userId,
            200,
            langCode
        )
        setJobList(jobs)
        setTotalJobs(jobTotal)
    }

    const getCompanyJobs = async (accessToken: string = '') => {
        if (!companyInfo?.companyId) {
            return
        }

        const { jobs, jobTotal, jobLocations } = await doGetJobList(
            companyInfo?.companyId || 0,
            accessToken,
            searchQuery,
            page,
            locationId,
            user?.userId,
            jobPerPage,
            langCode
        )

        setJobList([...jobs])
        setTotalJobs(jobTotal)
        setCityOptions(getLocalizedOptions(initialDataProvider.getLocationNameFromIds(jobLocations || [])))
        setIsHaveJobs(jobs.length > 0)
    }

    const onUpdateUser = (e: Event) => {
        if (!e.detail.user?.accessToken) {
            return
        }
        // get job list again (with user updated)
        if (!jobList.length) {
            getCompanyJobs(e.detail?.user?.accessToken)
        }
    }

    useEffect(() => {
        if (!isDidMountedRef.current) {
            return
        }
        goToTopJobList()
    }, [jobList, isDidMountedRef.current])

    // init job list
    useEffect(() => {
        getCompanyJobs(user?.accessToken || '')
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [companyInfo?.companyId, user])

    // delay search when keyword change
    useDidUpdateEffect(() => {
        setPage(0)
        doGetJobList(
            companyInfo?.companyId || 0,
            user?.accessToken || '',
            searchQuery,
            page,
            locationId,
            user?.userId,
            200,
            langCode
        ).then(({ jobs, jobTotal }) => {
            setJobList(jobs)
            setTotalJobs(jobTotal)
        })
    }, [debouncedQuery])

    useEffect(() => {
        window.addEventListener(UPDATE_USER, onUpdateUser)
        return () => {
            window.removeEventListener(UPDATE_USER, onUpdateUser)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const renderJobText = totalJobs > 1 ? `${totalJobs} ${t('jobs')}` : `${totalJobs} ${t('job')}`
    const userInfo = { ...user, jobLevelId: user?.jobLevelId[0], id: user?.id ?? 0 }
    return (
        <>
            <JobListWrapper>
                <JobListHeader isMobile={isMobile} ref={jobListRef}>
                    <JobListTitle id='OpeningJobHeader'>
                        {t('Opening Jobs')}&nbsp;<JobAmount>({renderJobText})</JobAmount>
                    </JobListTitle>
                </JobListHeader>

                {isHaveJobs && (
                    <Row gutter={16} style={{ marginBottom: 16 }}>
                        <Col sm={10} md={14}>
                            <SearchBox isDisplayRemoveIcon={searchQuery.length > 0}>
                                <Images.SearchIcon />
                                <SearchInput
                                    placeholder={t('Enter a job title')}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    value={searchQuery}
                                    isDisplayRemoveIcon={searchQuery.length > 0}
                                />
                                <SearchRemoveIcon onClick={() => setSearchQuery('')} isDisplay={searchQuery.length > 0}>
                                    <Images.RemoveFillIcon />
                                </SearchRemoveIcon>
                            </SearchBox>
                        </Col>
                        <Col xs={12} sm={7} md={5}>
                            <MultipleSelect
                                key={`multi-select-location-${locationId.join('-')}`}
                                type='multipleApply'
                                name='job-location'
                                langCode={langCode === 'vi' ? ELanguage.VI : ELanguage.EN}
                                selectPlaceholder={t('All locations')}
                                selectPlaceholderColor='#333333'
                                dropdownPlacement='bottom-end'
                                dropdownTopPosition={1}
                                options={cityOptions}
                                defaultSelect={locationId}
                                limit={3}
                                limitText={t('You have selected up to three locations')}
                                isMobile={isMobile}
                                onChange={(res) => handleLocationChange(res.value)}
                                floatingConfig={{
                                    position: 'absolute',
                                    useWindowAsFlippingContainer: true
                                }}
                            />
                        </Col>
                        <Col xs={12} sm={7} md={5}>
                            <MultipleRadio
                                name='job-per-page'
                                placeholder={t('Jobs per page')}
                                options={jobPerPageOptions}
                                value={jobPerPage}
                                isMobile={isMobile}
                                onChange={(res) => handleJobPerPageChange(res.value)}
                            />
                        </Col>
                    </Row>
                )}
                <JobCardWrapper>
                    {jobList && jobList.length > 0 ? (
                        <>
                            {jobList.map((item, index) => {
                                return (
                                    <JobCard
                                        key={`job-item-${item.jobId}`}
                                        data={item as any}
                                        langCode={langCode}
                                        isMobile={isMobile}
                                        isManagementJobPage={false}
                                        custom={{
                                            forceNormalJob: true,
                                            variant: 'primary'
                                        }}
                                        utm='utm_source_navi=CompanyProfileBasic&utm_medium_navi=JobList&utm_campaign_navi=JobTitle'
                                        previewJob={{
                                            appMode: process.env.NEXT_PUBLIC_APP_MODE,
                                            baseUrl: process.env.NEXT_PUBLIC_VNW_JOBSEEKER_URL || ''
                                        }}
                                        /* robot={{ */
                                        /*     enable: true, */
                                        /*     data: item */
                                        /* }} */
                                        api={{
                                            accessToken: decrypt(user?.accessToken || ''),
                                            baseUrl: process.env.NEXT_PUBLIC_MS_BASE_PATH || '',
                                            appId: process.env.VNW_APP_ID,
                                            source: 'Company container - Company detail - Opening jobs'
                                        }}
                                        userInfo={userInfo}
                                        classSelector='company_openingjobs'
                                        initData={{
                                            locations: initialDataProvider.getLocations(),
                                            jobFunctions: initialDataProvider.getJobFunction(),
                                            companyIndustries: initialDataProvider.getIndustries(),
                                            jobLevels: initialDataProvider.getLevels(),
                                            educations: initialDataProvider.getHighestDegrees(),
                                            countries: initialDataProvider.getCountries(),
                                            cities: initialDataProvider.getLocations(),
                                            districts: initialDataProvider.getDistricts(),
                                            benefits: initialDataProvider.getBenefits()
                                        }}
                                        headingTagTitleJob={'h3'}
                                    />
                                )
                            })}
                        </>
                    ) : (
                        <>
                            <EmptyDiv>
                                <ImageWrapper>
                                    <Image
                                        src={EMPTY_IMAGE}
                                        alt={t('There are currently no opening jobs.')}
                                        layout='fill'
                                    />
                                </ImageWrapper>
                                <p>{t('There are currently no opening jobs.')}</p>
                            </EmptyDiv>
                        </>
                    )}
                </JobCardWrapper>
            </JobListWrapper>

            {totalJobs > 0 &&
                jobList.length > 0 &&
                (enablePagination ? (
                    <div
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: isMobile ? 'center' : 'space-between',
                            flexWrap: 'wrap',
                            gap: 16
                        }}
                    >
                        <div style={{ display: 'inline-flex', alignItems: 'center', gap: 10 }}>
                            <Button
                                outline='none'
                                iconPosition='left'
                                isIcon
                                iconComponent={<NarrowArrow direction='left' />}
                                disabled={!page}
                                onClick={() => handleNavigatePage(page - 1)}
                            ></Button>
                            <Span name='body' color={colors.grey[40]} size='medium'>
                                {page + 1} / {Math.ceil(totalJobs / jobPerPage)}
                            </Span>
                            <Button
                                outline='none'
                                iconPosition='right'
                                isIcon
                                iconComponent={<NarrowArrow direction='right' />}
                                disabled={
                                    Math.floor(totalJobs / jobPerPage) < 1 ||
                                    Math.ceil(totalJobs / jobPerPage) === page + 1
                                }
                                onClick={() => handleNavigatePage(page + 1)}
                            ></Button>
                        </div>
                        <div style={{ width: isMobile ? '100%' : 250 }}>
                            <MultipleRadio
                                name='job-per-page'
                                placeholder={t('Jobs per page')}
                                options={jobPerPageOptions}
                                value={jobPerPage}
                                isMobile={isMobile}
                                onChange={(res) => handleJobPerPageChange(res.value)}
                            />
                        </div>
                    </div>
                ) : (
                    <ButtonWrapper isMobile={isMobile}>
                        <Button
                            outline='solid'
                            type='default'
                            style={{ width: 150 }}
                            onClick={() => handleViewMoreJob(page + 1)}
                        >
                            {t('View more')}
                        </Button>
                    </ButtonWrapper>
                ))}
        </>
    )
}

export default memo(JobList)
